import { useState } from 'react';
import {
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconMapPin, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api, ApiError } from '../lib/api';
import type { AddressSuggestion } from '../lib/api';

/**
 * Triage: Adress-Vorschläge der Techniker für Bestandskunden.
 * Freigabe schreibt die Adresse in den ServiceHub und schickt sie danach nach weclapp
 * (party.addresses[], Warteliste bei Ausfall — v1.385.0); Ablehnung informiert den
 * Einreicher (Glocke).
 */

function addressLine(s: AddressSuggestion): string {
  const zipCity = [s.postalCode, s.city].filter(Boolean).join(' ');
  return [s.street, s.street2, zipCity].filter(Boolean).join(', ');
}

function errorMessage(e: unknown): string {
  return e instanceof ApiError ? e.summary || e.message : e instanceof Error ? e.message : 'Unbekannter Fehler';
}

function SuggestionCard({ suggestion }: { suggestion: AddressSuggestion }) {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reviewNote, setReviewNote] = useState('');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['address-suggestions'] });
  };

  const approveMutation = useMutation({
    mutationFn: () => api.approveAddressSuggestion(suggestion.id, { reviewNote: reviewNote.trim() || undefined }),
    onSuccess: (res) => {
      invalidate();
      notifications.show({
        title: 'Adresse übernommen',
        message:
          `${addressLine(suggestion)} ist jetzt bei ${suggestion.customer.name} hinterlegt.` +
          (res.weclappPending ? ' weclapp folgt, sobald es antwortet.' : ''),
        color: res.weclappPending ? 'yellow' : 'teal',
      });
    },
    onError: (e: unknown) =>
      notifications.show({ title: 'Übernahme fehlgeschlagen', message: errorMessage(e), color: 'red' }),
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.rejectAddressSuggestion(suggestion.id, reviewNote.trim() || undefined),
    onSuccess: () => {
      invalidate();
      notifications.show({ title: 'Vorschlag abgelehnt', message: suggestion.customer.name, color: 'gray' });
    },
    onError: (e: unknown) =>
      notifications.show({ title: 'Ablehnung fehlgeschlagen', message: errorMessage(e), color: 'red' }),
  });

  const busy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <div style={{ minWidth: 0 }}>
            <Group gap={6} wrap="wrap">
              <Text fw={600}>{suggestion.customer.name}</Text>
              {suggestion.kind === 'UPDATE' && <Badge size="xs" color="orange" variant="light">Korrektur</Badge>}
              {suggestion.proposePrimary && <Badge size="xs" color="violet" variant="light">Hauptanschrift</Badge>}
              {suggestion.proposeDelivery && <Badge size="xs" color="blue" variant="light">Lieferanschrift</Badge>}
              {suggestion.source !== 'TECHNICIAN' && (
                <Badge size="xs" color="grape" variant="light">{suggestion.source}</Badge>
              )}
            </Group>
            <Text size="sm" mt={4}>
              {addressLine(suggestion)}
              {suggestion.company ? ` (${suggestion.company})` : ''}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              {new Date(suggestion.createdAt).toLocaleString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              · von {suggestion.submittedBy?.displayName ?? 'Unbekannt'}
              {!suggestion.customer.weclappCustomerId && ' · noch nicht in weclapp'}
            </Text>
            {suggestion.note && (
              <Text size="xs" c="dimmed" fs="italic" mt={2}>
                „{suggestion.note}"
              </Text>
            )}
          </div>
          <Group gap="xs">
            <Tooltip
              label="Speichert die Adresse im ServiceHub und schickt sie danach nach weclapp"
              withArrow
            >
              <Button
                size="compact-sm"
                color="teal"
                leftSection={<IconCheck size={14} />}
                loading={approveMutation.isPending}
                disabled={busy}
                onClick={() => approveMutation.mutate()}
              >
                Übernehmen
              </Button>
            </Tooltip>
            <Button
              size="compact-sm"
              variant="light"
              color="red"
              leftSection={<IconX size={14} />}
              loading={rejectMutation.isPending}
              disabled={busy}
              onClick={() => (rejecting ? rejectMutation.mutate() : setRejecting(true))}
            >
              {rejecting ? 'Ablehnung bestätigen' : 'Ablehnen'}
            </Button>
          </Group>
        </Group>
        {rejecting && (
          <Textarea
            placeholder="Begründung für den Einreicher (optional)"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.currentTarget.value)}
            autosize
            minRows={1}
          />
        )}
      </Stack>
    </Paper>
  );
}

export function AddressSuggestionsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['address-suggestions'],
    queryFn: () => api.listAddressSuggestions(),
    staleTime: 30_000,
  });

  const suggestions = data?.suggestions ?? [];
  const pending = suggestions.filter((s) => s.status === 'PENDING');
  const decided = suggestions.filter((s) => s.status !== 'PENDING').slice(0, 20);

  return (
    <Container size="md" py="md">
      <Stack gap="md">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="teal">
            <IconMapPin size={16} />
          </ThemeIcon>
          <Title order={2}>Adress-Vorschläge</Title>
          {pending.length > 0 && <Badge color="orange">{pending.length} offen</Badge>}
        </Group>
        <Text size="xs" c="dimmed">
          Techniker schlagen Adressen für Bestandskunden vor (z. B. aus dem Anfahrt-Schritt).
          Die Freigabe speichert die Anschrift im ServiceHub und schickt sie danach nach weclapp —
          antwortet weclapp nicht, geht sie automatisch hinterher (Admin → Sync).
        </Text>

        {isLoading && (
          <Group justify="center" py="lg">
            <Loader size="sm" />
          </Group>
        )}

        {!isLoading && pending.length === 0 && (
          <Paper withBorder p="lg" radius="md">
            <Text size="sm" c="dimmed" ta="center">
              Keine offenen Adress-Vorschläge. 🎉
            </Text>
          </Paper>
        )}

        {pending.map((s) => (
          <SuggestionCard key={s.id} suggestion={s} />
        ))}

        {decided.length > 0 && (
          <>
            <Title order={4} mt="md">Zuletzt entschieden</Title>
            {decided.map((s) => (
              <Paper key={s.id} withBorder p="sm" radius="md" opacity={0.75}>
                <Group justify="space-between" wrap="wrap" gap="xs">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {s.customer.name} — {addressLine(s)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.status === 'APPROVED' ? 'Übernommen' : 'Abgelehnt'} von{' '}
                      {s.reviewedBy?.displayName ?? '—'}
                      {s.reviewedAt
                        ? ` · ${new Date(s.reviewedAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`
                        : ''}
                      {s.reviewNote ? ` · „${s.reviewNote}"` : ''}
                    </Text>
                  </div>
                  <Badge color={s.status === 'APPROVED' ? 'teal' : 'red'} variant="light">
                    {s.status === 'APPROVED' ? 'Übernommen' : 'Abgelehnt'}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </>
        )}
      </Stack>
    </Container>
  );
}
